import { isOfferTrial, isBasicPlanOffer, isApiDietTargetSystem } from "./helpers"

// Ideally, naming from pipeline config should match with used in common code.
// But historically it's not the case, so we need this adapter.
const pricingKeysAdapterDefault = (key) => {
  const pricingKeysAdapter = {
    "price_no_discount": "price_no_discount",
    "price_per_period_no_discount": "price_per_period_no_discount",
    "price": "total_price",
    "price_per_period": "price_per_period",
    "price_discount": "price_discount",
    "price_discount_amount": "price_discount_amount",
    "chance_price": "chance_price",
    "chance_price_per_period": "chance_price_per_period",
    "chance_price_discount": "chance_price_discount",
    "chance_price_discount_amount": "chance_price_discount_amount",
    "trial_price": "trial_price",
    "trial_price_per_period": "trial_price_per_period",
    "trial_price_discount": "trial_price_discount",
    "trial_price_without_discount": "trial_price_without_discount",
    "trial_price_discount_amount": "trial_price_discount_amount",
    "chance_trial_price": "chance_trial_price",
    "chance_trial_price_per_period": "chance_trial_price_per_period",
    "chance_trial_price_discount": "chance_trial_price_discount",
    "chance_trial_price_discount_amount": "chance_trial_price_discount_amount",
  }

  return pricingKeysAdapter[key] || key
}

//
// Helpers for prices array API
// (next iteration of API improvements after offers custom data)
//

// Types of prices included to the offer prices array
export const PRICE_TYPES = {
  TRIAL: "trial",
  CHANCE_TRIAL: "chance_trial",
  STANDARD: "standard",
  CHANCE: "chance",
}

// Gets the property value from the offer prices array
const getPriceProperty = ({ offer, priceType, property }) => {
  return offer.prices?.find((price) => price.type === priceType)?.[property] || null
}

// Determines the proper price type from the offer prices array
// that should be used based on the current offer conditions
const getOfferPriceType = ({ offer, isChance }) => {
  // Upsells are always standard type
  if (!isBasicPlanOffer(offer)) {
    return PRICE_TYPES.STANDARD
  }

  const isTrial = isOfferTrial(offer)

  if (isTrial) {
    return isChance
      ? PRICE_TYPES.CHANCE_TRIAL
      : PRICE_TYPES.TRIAL
  } else {
    return isChance
      ? PRICE_TYPES.CHANCE
      : PRICE_TYPES.STANDARD
  }
}

// Determines the proper offerPriceId needed for the BE to
// precess the payment correctly to the given offer type
const getOfferPriceId = ({ offer, isChance }) => {
  // Determine if offer is trial and if exit intent chance applied
  const priceType = getOfferPriceType({ offer, isChance })

  return getPriceProperty({
    offer,
    priceType,
    property: "id",
  })
}

//
// Helpers for offers custom data and UI
//

const addCorrectCurrencyLabel = ({
  price,
  symbol
}) => {
  if (!price) return

  return symbol.trim() === '$'
    ? `${symbol}${price}`
    : `${price}${symbol}`
}


const addPricePeriod = ({ price, period }) => {
  let periodFormatted = period && period.trim()
  if (periodFormatted && !periodFormatted.startsWith('/')) {
    periodFormatted = ` ${periodFormatted}`
  }

  return `${price}${periodFormatted ? periodFormatted : ""}`
}

const getPricePerPeriodValue = ({ offer, isChance, keysAdapter, isWithoutTrial }) => {
  const price = getPricePerPeriod({ offer, isChance, keysAdapter, isWithoutTrial })
  const match = price.match(/\d+([.,]\d+)?/)
  const normalized = match[0].replace(',', '.')
  return parseFloat(normalized)
}

const isBestDeal = ({ offer, offers, isChance, keysAdapter, isWithoutTrial }) => {
  const priceValue = getPricePerPeriodValue({ offer, isChance, keysAdapter, isWithoutTrial })

  const othersOffersPerPeriodPrices = offers.map((o) => {
    return getPricePerPeriodValue({ offer: o, isChance, keysAdapter, isWithoutTrial })
  })

  const min = Math.min(...othersOffersPerPeriodPrices)

  // Consider that biggest value should not have duplicates
  return (
    priceValue === min &&
    othersOffersPerPeriodPrices.lastIndexOf(priceValue) ===
    othersOffersPerPeriodPrices.indexOf(priceValue)
  )
}

const isMaxPriceDeal = ({ offer, offers, isChance, keysAdapter, isWithoutTrial }) => {
  const priceValue = getPricePerPeriodValue({ offer, isChance, keysAdapter, isWithoutTrial })

  const othersOffersPerPeriodPrices = offers.map((o) => {
    return getPricePerPeriodValue({ offer: o, isChance, keysAdapter, isWithoutTrial })
  })

  const max = Math.max(...othersOffersPerPeriodPrices)

  return (
    priceValue === max &&
    othersOffersPerPeriodPrices.lastIndexOf(priceValue) ===
    othersOffersPerPeriodPrices.indexOf(priceValue)
  )
}

//
// Common standardized functions to get proper price labels for UI
//

// Prices without discount
// Used for all cases to illustrate the highest price.
// Configurable by products from cppv3 admin.
const getPriceNoDiscount = ({ offer, keysAdapter }) => {
  const { customData } = offer

  return addCorrectCurrencyLabel({
    price: customData[keysAdapter('price_no_discount')],
    symbol: customData.symbol,
  })
}

const getPricePerPeriodNoDiscount = ({ offer, keysAdapter }) => {
  const { customData } = offer
  const isTrial = isOfferTrial(offer)

  const price = isTrial
    ? customData[keysAdapter('price_per_period_no_discount')]
    : customData[keysAdapter('price_per_period_no_discount')]

  return addCorrectCurrencyLabel({
    price,
    symbol: customData.symbol,
  })
}

const getPriceNoDiscountRecurring = ({ offer, keysAdapter }) => {
  const { customData } = offer

  const priceNoDiscount = getPriceNoDiscount({
    offer,
    keysAdapter
  })

  return `${priceNoDiscount}${customData.period ? " " + customData.period : ""}`
}

// Gets any price without conditions
const getPriceDataValue = ({ offer, dataKey, keysAdapter }) => {
  const keysToOmitCurrencyLabel = [
    'price_discount',
    'chance_price_discount',
    'trial_price_discount',
    'chance_trial_price_discount',
  ]
  const { customData } = offer

  const pricingData = customData[keysAdapter(dataKey)]

  if (keysToOmitCurrencyLabel.includes(dataKey)) return pricingData

  return addCorrectCurrencyLabel({
    price: pricingData,
    symbol: customData.symbol,
  })
}

// Gets the actual price considering all the conditions
const getPrice = ({ offer, isChance, keysAdapter, isWithoutTrial }) => {
  const { customData } = offer
  const isTrial = isOfferTrial(offer)
  const offerChanceKey = isApiDietTargetSystem(offer) ? 'chance_price' : 'exit_intent_price'
  const trialOfferChanceKey = isApiDietTargetSystem(offer) ? 'chance_trial_price' : 'exit_intent_trial_price'

  let price

  if (isTrial && !isWithoutTrial) {
    price = isChance
      ? customData[keysAdapter(trialOfferChanceKey)]
      : customData[keysAdapter('trial_price')]
  } else {
    price = isChance
      ? customData[keysAdapter(offerChanceKey)]
      : customData[keysAdapter('price')]
  }

  return addCorrectCurrencyLabel({
    price,
    symbol: customData.symbol,
  })
}

// Product requirements
// Trial: standard price
// Trial exit intent: standard price
// Regular: standard price
// Regular exit intent: standard chance price
const getFuturePrice = ({ offer, isChance, keysAdapter }) => {
  const isTrial = isOfferTrial(offer)

  if (isTrial) {
    return getPriceDataValue({ offer, dataKey: 'price', keysAdapter })
  } else {
    return getPrice({ offer, isChance, keysAdapter })
  }
}

// Product requirements
// Trial: standard price
// Trial exit intent: standard price
// Regular: standard price
// Regular exit intent: standard chance price
const getFuturePricePerPeriod = ({ offer, isChance, keysAdapter }) => {
  const { customData } = offer
  const isTrial = isOfferTrial(offer)

  let price

  if (isTrial) {
    price = customData[keysAdapter('price_per_period')]
  } else {
    price = isChance
      ? customData[keysAdapter('chance_price_per_period')]
      : customData[keysAdapter('price_per_period')]
  }

  const priceWithCurrencyLabel = addCorrectCurrencyLabel({
    price,
    symbol: customData.symbol,
  })

  return addPricePeriod({
    price: priceWithCurrencyLabel,
    period: customData.period
  })
}

// standard period price
const getPricePerPeriod = ({ offer, isChance, keysAdapter, isWithoutTrial }) => {
  const { customData } = offer
  const isTrial = isOfferTrial(offer)

  let price

  if (isTrial && !isWithoutTrial) {
    price = isChance
      ? customData[keysAdapter('chance_trial_price_per_period')]
      : customData[keysAdapter('trial_price_per_period')]
  } else {
    price = isChance
      ? customData[keysAdapter('chance_price_per_period')]
      : customData[keysAdapter('price_per_period')]
  }

  return addCorrectCurrencyLabel({
    price,
    symbol: customData.symbol,
  })
}

// Determines if offer is subscription based on agreed approach with product team:
// If offer has the `period` custom data field filled - than this is a subscription
const isPricingRecurring = ({ offer }) => {
  const { customData } = offer

  return customData.period && customData.period.trim()
}

const getPriceRecurring = ({ offer, keysAdapter }) => {
  const { customData } = offer

  const price = getPrice({
    offer,
    keysAdapter
  })

  return addPricePeriod({
    price,
    period: customData.period
  })
}

const getPriceDiscount = ({ offer, isChance, keysAdapter, isWithoutTrial }) => {
  const { customData } = offer
  const isTrial = isOfferTrial(offer)

  let discount

  if (isTrial && !isWithoutTrial) {
    discount = isChance
      ? customData[keysAdapter('chance_trial_price_discount')]
      : customData[keysAdapter('trial_price_discount')]
  } else {
    discount = isChance
      ? customData[keysAdapter('chance_price_discount')]
      : customData[keysAdapter('price_discount')]
  }

  return discount
}

const getPriceDiscountAmount = ({ offer, isChance, keysAdapter, isWithoutTrial }) => {
  const { customData } = offer
  const isTrial = isOfferTrial(offer)

  let price

  if (isTrial && !isWithoutTrial) {
    price = isChance
      ? customData[keysAdapter('chance_trial_price_discount_amount')]
      : customData[keysAdapter('trial_price_discount_amount')]
  } else {
    price = isChance
      ? customData[keysAdapter('chance_price_discount_amount')]
      : customData[keysAdapter('price_discount_amount')]
  }

  return addCorrectCurrencyLabel({
    price,
    symbol: customData.symbol,
  })
}

// Special export to allow init all pricing helpers with the keys adapter at once.
// Useful to no pass adapter every time in place of usage.
export const initPricingHelpers = (keysAdapter = pricingKeysAdapterDefault) => {
  // Returns fn which calls helper with defined adapter and passed args
  const init = (helper) => (argsObj) => helper({ keysAdapter, ...argsObj })

  return {
    isBestDeal: init(isBestDeal),
    isMaxPriceDeal: init(isMaxPriceDeal),
    addPricePeriod: init(addPricePeriod),
    getPriceNoDiscount: init(getPriceNoDiscount),
    getPricePerPeriodNoDiscount: init(getPricePerPeriodNoDiscount),
    getPriceNoDiscountRecurring: init(getPriceNoDiscountRecurring),
    getPriceDataValue: init(getPriceDataValue),
    getPrice: init(getPrice),
    getFuturePrice: init(getFuturePrice),
    getFuturePricePerPeriod: init(getFuturePricePerPeriod),
    getPricePerPeriod: init(getPricePerPeriod),
    getPriceRecurring: init(getPriceRecurring),
    isPricingRecurring: init(isPricingRecurring),
    getPriceDiscount: init(getPriceDiscount),
    getPriceDiscountAmount: init(getPriceDiscountAmount),
    getOfferPriceType,
    getPriceProperty,
    isOfferTrial,
    getOfferPriceId,

  };
}

// Export to init individual helpers if needed.
// May be useful to not pass adapter every time in place of usage.
export const initPricingHelper = ({ keysAdapter, name }) => {
  const helpers = {
    isBestDeal,
    isMaxPriceDeal,
    addPricePeriod,
    getPriceNoDiscount,
    getPricePerPeriodNoDiscount,
    getPriceNoDiscountRecurring,
    getPriceDataValue,
    getPrice,
    getFuturePrice,
    getFuturePricePerPeriod,
    getPricePerPeriod,
    getPriceRecurring,
    isPricingRecurring,
    getPriceDiscount,
    getPriceDiscountAmount,
    getOfferPriceType,
    getPriceProperty,
    isOfferTrial,
    getOfferPriceId,
  };

  return (argsObj) => helpers[name]({ keysAdapter, ...argsObj })
}

// Regular export of raw helpers
export {
  addCorrectCurrencyLabel,
  isBestDeal,
  isMaxPriceDeal,
  addPricePeriod,
  getPriceNoDiscount,
  getPricePerPeriodNoDiscount,
  getPriceNoDiscountRecurring,
  getPriceDataValue,
  getPrice,
  getFuturePrice,
  getFuturePricePerPeriod,
  getPricePerPeriod,
  getPriceRecurring,
  isPricingRecurring,
  getPriceDiscount,
  getPriceDiscountAmount,
  getOfferPriceType,
  getPriceProperty,
  isOfferTrial,
  getOfferPriceId,
}
